/**
 * Authentication Context Definition
 *
 * Separates context definition from provider for better fast refresh support.
 */

import { createContext } from 'react';
import type { User } from 'firebase/auth';

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /** Send verify-and-change-email link to newEmail (custom Resend flow). Requires signed-in user. */
  sendChangeEmailVerification: (newEmail: string) => Promise<void>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateUserProfile: (displayName?: string, photoURL?: string) => Promise<void>;
  /** Link Google as an additional sign-in method. Throws ProviderLinkConflictError or ReauthRequiredError. */
  linkProviderWithGoogle: () => Promise<void>;
  /** Link GitHub as an additional sign-in method (cross-email OK). Throws ProviderLinkConflictError or ReauthRequiredError. */
  linkProviderWithGithub: () => Promise<void>;
  /** Add email/password sign-in to an OAuth-only account. Throws ReauthRequiredError if session is stale. */
  addPassword: (password: string) => Promise<void>;
  /** Unlink a sign-in provider. Clears githubUsername claim when unlinking GitHub. Throws ReauthRequiredError if stale. */
  unlinkProvider: (providerId: string) => Promise<void>;
  /** Re-send the email verification link. Signs in briefly to obtain a User object, sends the link, then signs out. */
  resendEmailVerification: (email: string, password: string) => Promise<void>;
  /** Re-authenticate using email/password — required before sensitive operations. */
  reauthenticateWithPassword: (password: string) => Promise<void>;
  /** Re-authenticate using Google OAuth popup — required before sensitive operations. */
  reauthenticateWithGoogle: () => Promise<void>;
  /** Re-authenticate using GitHub OAuth popup — required before sensitive operations. */
  reauthenticateWithGithub: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);
